import React, { useEffect, useMemo, useState } from "react";
import { Layers, Maximize2, Minimize2, RotateCw, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const HEADERS = [
  { value: "minimal", label: "Minimal" },
  { value: "downtown", label: "Downtown" },
  { value: "urban-centered", label: "Urban Centered" },
  { value: "news-magazine", label: "News Magazine" },
  { value: "split-magazine", label: "Split Magazine" },
];

const FOOTERS = [
  { value: "minimal", label: "Minimal" },
  { value: "navy-brand", label: "Navy Brand" },
  { value: "dark-editorial", label: "Dark Editorial" },
  { value: "centered-magazine", label: "Centered Magazine" },
  { value: "split-magazine", label: "Split Magazine" },
];

const TEMPLATES = [
  { template: "tribune", variants: ["v1", "v2", "v3"] },
  { template: "terminal", variants: ["v1", "v2", "v3"] },
  { template: "brief", variants: ["v1", "v2", "v3"] },
  { template: "magazine", variants: ["v1", "v2", "v3"] },
];

const SIZE_PRESETS = [
  { id: "thumb", label: "Thumb", w: 360, h: 540, scale: 0.28125 }, // 1280→360
  { id: "preview", label: "Preview", w: 640, h: 800, scale: 0.5 },
  { id: "full", label: "Full", w: 1280, h: 900, scale: 1 },
];

/**
 * Admin Template Gallery — renders every header / footer / layout variant
 * against the selected site so visual issues are caught in one scroll instead
 * of 180 manual page loads.
 *
 * Each tile fires an iframe at the public SSR endpoint with admin-only
 * `?preview_*=` query params that override the site's stored variant in-memory
 * (server-side gate in `routes/render._apply_preview_overrides`). Public
 * visitors don't see any of this.
 */
export default function TemplateGallery() {
  const [sites, setSites] = useState([]);
  const [siteSlug, setSiteSlug] = useState("");
  const [size, setSize] = useState(SIZE_PRESETS[0]);
  const [bust, setBust] = useState(0);

  useEffect(() => {
    (async () => {
      const r = await api.get("/sites").catch(() => null);
      const list = r?.data?.items || r?.data || [];
      setSites(list);
      const slug = list.find((s) => s.slug === "finances-4-smart")?.slug || list[0]?.slug || "";
      setSiteSlug(slug);
    })();
  }, []);

  const previewBase = useMemo(() => {
    if (!siteSlug) return "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/render/sites/${siteSlug}/`;
  }, [siteSlug]);

  function url(params) {
    if (!previewBase) return "";
    const p = new URLSearchParams(params);
    p.set("_b", bust);
    return `${previewBase}?${p.toString()}`;
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Layers className="w-5 h-5 text-amber-500" />
            <h1 className="font-display text-2xl font-black tracking-tight">Template Gallery</h1>
          </div>
          <p className="text-sm text-zinc-400 max-w-2xl">
            Live SSR previews of every header / footer / layout variant against the selected site.
            Each iframe uses admin-only <code className="text-amber-400 mono">?preview_*</code> overrides — public visitors see the site&apos;s real config.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={siteSlug}
            onChange={(e) => setSiteSlug(e.target.value)}
            className="bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
            data-testid="gallery-site-select"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.slug}>{s.name || s.slug}</option>
            ))}
          </select>
          <div className="flex border border-zinc-800 rounded-sm overflow-hidden">
            {SIZE_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setSize(p)}
                data-testid={`gallery-size-${p.id}`}
                className={`px-3 py-2 text-xs font-semibold ${size.id === p.id ? "bg-amber-500/20 text-amber-200" : "text-zinc-400 hover:bg-zinc-900"}`}
              >
                {p.id === "full" ? <Maximize2 className="w-3.5 h-3.5 inline" /> : p.id === "thumb" ? <Minimize2 className="w-3.5 h-3.5 inline" /> : null}
                <span className="ml-1.5">{p.label}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setBust(Date.now())}
            data-testid="gallery-refresh"
            className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-200 px-3 py-2 rounded-sm text-xs font-semibold"
            title="Cache-bust all iframes"
          >
            <RotateCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {!siteSlug && (
        <div className="text-sm text-zinc-500 mono">No site selected.</div>
      )}

      {siteSlug && (
        <>
          <Section
            title="Headers"
            subtitle="Each tile varies ONLY the header; footer + layout stay at the site's real config."
            items={HEADERS}
            buildUrl={(it) => url({ preview_header: it.value })}
            size={size}
          />
          <Section
            title="Footers"
            subtitle="Each tile varies ONLY the footer."
            items={FOOTERS}
            buildUrl={(it) => url({ preview_footer: it.value })}
            size={size}
          />
          <LayoutSection size={size} buildUrl={(t, v) => url({ preview_template: t, preview_variant: v })} />
        </>
      )}
    </div>
  );
}

function Section({ title, subtitle, items, buildUrl, size }) {
  return (
    <section className="space-y-3">
      <header className="border-b border-zinc-800 pb-2">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <span className="text-[11px] text-zinc-500 mono">{items.length} variants</span>
        </div>
        <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>
      </header>
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${size.w + 16}px, 1fr))` }}>
        {items.map((it) => (
          <PreviewTile
            key={it.value}
            label={it.label}
            badge={it.value}
            href={buildUrl(it)}
            size={size}
          />
        ))}
      </div>
    </section>
  );
}

function LayoutSection({ buildUrl, size }) {
  return (
    <section className="space-y-3">
      <header className="border-b border-zinc-800 pb-2">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-display text-lg font-bold">Layouts (template × variant)</h2>
          <span className="text-[11px] text-zinc-500 mono">{TEMPLATES.reduce((s, t) => s + t.variants.length, 0)} variants</span>
        </div>
        <div className="text-xs text-zinc-500 mt-1">Each tile varies template + variant; header + footer stay at the site&apos;s real config.</div>
      </header>
      {TEMPLATES.map((t) => (
        <div key={t.template}>
          <div className="text-xs text-zinc-400 mono uppercase tracking-wide mt-3 mb-2">{t.template}</div>
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${size.w + 16}px, 1fr))` }}>
            {t.variants.map((v) => (
              <PreviewTile
                key={v}
                label={`${t.template} · ${v}`}
                badge={`${t.template}/${v}`}
                href={buildUrl(t.template, v)}
                size={size}
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function PreviewTile({ label, badge, href, size }) {
  const innerW = 1280;
  const innerH = Math.round(size.h / size.scale);
  return (
    <div
      data-testid={`gallery-tile-${badge}`}
      className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950 group hover:border-amber-500/40 transition"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-zinc-800 bg-zinc-900/50">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{label}</div>
          <div className="text-[10px] text-zinc-500 mono">{badge}</div>
        </div>
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="text-zinc-500 hover:text-amber-400 shrink-0"
          title="Open full size in new tab"
          data-testid={`gallery-open-${badge}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
      <div style={{ width: size.w, height: size.h, overflow: "hidden", background: "#fff" }}>
        <iframe
          src={href}
          title={label}
          loading="lazy"
          style={{
            width: innerW,
            height: innerH,
            border: 0,
            transform: `scale(${size.scale})`,
            transformOrigin: "top left",
          }}
        />
      </div>
    </div>
  );
}
